/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '~/components/UI/shadcn/dialog'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
  CarouselPrevious,
  CarouselNext
} from '~/components/UI/shadcn/carousel'

import ArrowsExpandDiagonal from '../icons/arrows-expand-diagonal'
import { useCallback, useEffect, useState } from 'react'
import Loader from '~/components/UI/General/Loader/Loader'
import { isDevelopment } from '~/utils/misc'
import BlockVideo from '~/components/Media/components/BlockVideo'
import { cn } from '~/lib/utils'

interface ImageGalleryProps {
  images: Image[]
  selectedSlide: number
  onSlideChange: (index: any) => void
  onClose?: () => void
}

type Image = {
  image: {
    altText: string
    asset: { sys: { uri: string } }
    caption: string
  }
  video?: {
    sys: { uri: string }
  } | null
}

const PLACEHOLDER_SRC = `data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs%3D`

const ImageModal = (props: ImageGalleryProps) => {
  const { images, selectedSlide, onSlideChange, onClose } = props

  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  const [caption, setCaption] = useState('')
  const [, setIsModalOpen] = useState(false)

  const [slidesInView, setSlidesInView] = useState<number[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateSlidesInView = useCallback((api: any) => {
    setSlidesInView(slidesInView => {
      if (slidesInView.length === api.slideNodes().length) {
        api.off('slidesInView', updateSlidesInView)
      }
      const inView = api
        .slidesInView()
        .filter((index: number) => !slidesInView.includes(index))
      return slidesInView.concat(inView)
    })
  }, [])

  useEffect(() => {
    if (api && selectedSlide !== undefined) {
      api.scrollTo(selectedSlide) // Scroll to the selected slide
      setCurrent(selectedSlide) // Update the current state
    }
  }, [api, selectedSlide])

  useEffect(() => {
    if (!api) return

    updateSlidesInView(api)

    api.on('slidesInView', updateSlidesInView)
    api.on('reInit', updateSlidesInView)
  }, [api, updateSlidesInView])

  // Set current slide on load
  useEffect(() => {
    // If no api, return
    if (!api) return

    // Update current slide on select
    const updateCarouselState = () => {
      const newCurrent = api.selectedScrollSnap()
      setCurrent(newCurrent)
      onSlideChange?.(newCurrent)
    }

    // Call updateCarouselState on load
    updateCarouselState()

    // Listen for select event
    api.on('select', updateCarouselState)

    return () => {
      // Clean up on unmount
      api.off('select', updateCarouselState) // Clean up on unmount
    }
  }, [api, onSlideChange])

  // Set caption on load
  useEffect(() => {
    if (images[current]) {
      setCaption(images[current].image.caption)
    }
  }, [current, images])

  const setLoaded = useCallback(() => {
    if (slidesInView) setHasLoaded(true)
  }, [slidesInView, setHasLoaded])

  const handleModalChange = (isOpen: boolean) => {
    setIsModalOpen(isOpen) // Update modal open state
    if (!isOpen) {
      setCurrent(0)
      onClose?.()
    }
  }

  return (
    <Dialog onOpenChange={handleModalChange}>
      <DialogTrigger asChild>
        <button className='bg-blue-l lg:size-l text-h2-reg-sm relative z-[1] flex cursor-pointer items-center justify-center rounded-full sm:size-[36px]'>
          <ArrowsExpandDiagonal className='size-xs lg:size-s' />
        </button>
      </DialogTrigger>

      <DialogContent
        className='border-0 bg-transparent !p-0 shadow-none'
        gallery={true}
      >
        <DialogHeader>
          <DialogTitle className='sr-only'>Image Gallery</DialogTitle>
        </DialogHeader>

        <div className='h-full max-w-[80vw] lg:max-w-[60vw]'>
          <Carousel
            setApi={setApi}
            className='relative'
            opts={{
              align: 'start',
              loop: true
            }}
          >
            <div className='[&_button]:stroke-blue-d z-10 w-full fill-white text-white [&_button]:bg-white [&_button]:fill-black [&_input]:hover:border-white [&_input]:focus:border-white'>
              <div className='space-x-2xs hover:bg-none'>
                <CarouselPrevious
                  variant='default'
                  className='disabled:bg-grey-m disabled:stroke-blue-d w-l h-l absolute top-1/2 left-0 z-10 -translate-x-1/2 -translate-y-[calc(50%+100px)] border-none hover:bg-current disabled:opacity-100'
                />
                <CarouselNext
                  variant='default'
                  className='disabled:bg-grey-m disabled:stroke-blue-d w-l h-l absolute top-1/2 right-0 z-10 translate-x-1/2 -translate-y-[calc(50%+100px)] border-none hover:bg-current disabled:opacity-100'
                />
              </div>
            </div>

            <div
              className={
                'gap-xs grid h-full grid-rows-[auto_100px] place-items-center'
              }
            >
              {/* !! The above line may need adjusting !! */}
              {!hasLoaded && <Loader fill={'#f5b6dc'} />}
              <CarouselContent className=''>
                {images.map((item, index) => (
                  <CarouselItem
                    key={`list-${index}`}
                    className='relative aspect-video max-h-[800px]'
                  >
                    {item.video ? (
                      <BlockVideo
                        videoUrl={item.video.sys.uri}
                        width={''}
                        height={''}
                        image={item.image}
                        btnPosition='bottom-right'
                        btnClassName={cn(
                          '!right-m !bottom-m transition-all duration-500',
                          current !== index
                            ? 'invisible opacity-0'
                            : 'visible opacity-100'
                        )}
                        pause={current !== index}
                      />
                    ) : (
                      <img
                        src={
                          slidesInView.indexOf(index) > -1
                            ? (isDevelopment
                                ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                                : '') +
                              item.image.asset.sys.uri.replace(
                                /&crop=[^&]+/,
                                ''
                              )
                            : PLACEHOLDER_SRC
                        }
                        alt={item.image.altText}
                        className='h-full w-full object-contain'
                        data-src={item.image.asset.sys.uri}
                        onLoad={setLoaded}
                        width={900}
                        height={700}
                      />
                    )}
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className='py-xs min-h-2xl max-w-[75ch] text-center text-pretty text-white italic'>
                {caption}
              </div>
              <DialogFooter className='py-xs min-h-2xl max-w-[75ch] text-center text-pretty text-white italic'></DialogFooter>
            </div>
          </Carousel>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImageModal
